/**
 * Чистые хелперы страницы «Аналитика производства».
 * Без импортов React и MUI — формат чисел, период, фильтры секции, query.
 */

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Значение фильтра «без цвета»: в API `color: null`, в исходнике ключ «—». */
export const NO_COLOR = '—';

export const ANALYTICS_TABS = ['stages', 'sewing', 'payroll'];

function finite(n) {
  const v = Number(n);
  return Number.isFinite(v) ? v : 0;
}

export function fmtInt(n) {
  return Math.round(finite(n)).toLocaleString('ru-RU');
}

export function fmtMoney(n) {
  return `${Math.round(finite(n)).toLocaleString('ru-RU')} ₽`;
}

export function fmtKg(n, dec = 1) {
  const digits = Number.isFinite(Number(dec)) ? Number(dec) : 1;
  return finite(n).toLocaleString('ru-RU', { maximumFractionDigits: digits });
}

/** Количество в единицах процесса: кг — с десятыми, остальное — целое. */
export function fmtQty(n, unit) {
  return unit === 'кг' ? fmtKg(n) : fmtInt(n);
}

/** YYYY-MM-DD → DD.MM для осей графика. */
export function shortDate(ymd) {
  const s = String(ymd || '');
  return YMD_RE.test(s) ? `${s.slice(8, 10)}.${s.slice(5, 7)}` : s;
}

/** YYYY-MM-DD → DD.MM.YYYY. */
export function longDate(ymd) {
  const s = String(ymd || '');
  return YMD_RE.test(s) ? `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}` : s;
}

export function colorFilterKey(color) {
  if (color == null || color === '' || color === NO_COLOR) return NO_COLOR;
  return String(color);
}

function pad(n) {
  return String(n).padStart(2, '0');
}

function toYmd(date) {
  if (typeof date === 'string' && YMD_RE.test(date)) return date;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function parseYmd(ymd) {
  const [y, m, d] = String(ymd).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function addDays(ymd, delta) {
  const d = parseYmd(ymd);
  d.setDate(d.getDate() + delta);
  return toYmd(d);
}

/**
 * Быстрый диапазон включительно. `today` — Date или YYYY-MM-DD.
 * 7d / 30d — последние 7 / 30 дней, заканчивая today.
 */
export function quickRange(kind, today) {
  const to = toYmd(today);
  if (!to) return { from: null, to: null };
  if (kind === 'today') return { from: to, to };
  if (kind === 'yesterday') {
    const y = addDays(to, -1);
    return { from: y, to: y };
  }
  if (kind === '7d') return { from: addDays(to, -6), to };
  if (kind === '30d') return { from: addDays(to, -29), to };
  if (kind === 'month') {
    const d = parseYmd(to);
    return { from: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`, to };
  }
  return { from: to, to };
}

/** Подпись дня на графике: при total > 15 — каждая 5-я и последняя. */
export function trendLabelVisible(index, total) {
  const n = Number(total) || 0;
  const i = Number(index) || 0;
  if (n <= 15) return true;
  return i % 5 === 0 || i === n - 1;
}

/**
 * Фильтры модели и цвета внутри секции — как procBody в исходнике.
 * Цвет пересчитывает total по выбранному цвету и убирает нулевые строки.
 * Вес при фильтре цвета не показываем: в ответе он не разложен по цветам.
 */
export function applyItemFilters(items, { model, color } = {}) {
  let list = (Array.isArray(items) ? items : []).map((it) => ({
    ...it,
    by_color: Array.isArray(it?.by_color) ? it.by_color.map((row) => ({ ...row })) : [],
  }));
  if (model) list = list.filter((it) => it.model === model);
  if (color) {
    const key = colorFilterKey(color);
    list = list
      .map((it) => {
        const byColor = it.by_color.filter((row) => colorFilterKey(row.color) === key);
        const total = byColor.reduce((sum, row) => sum + finite(row.qty), 0);
        return { ...it, total, by_color: byColor, weight_kg: null };
      })
      .filter((it) => finite(it.total) > 0);
  }
  list.sort((a, b) => finite(b.total) - finite(a.total));
  return list;
}

export function hasAnyColor(items) {
  return (Array.isArray(items) ? items : []).some((it) =>
    (it?.by_color || []).some((row) => colorFilterKey(row?.color) !== NO_COLOR),
  );
}

export function parseQuery(search) {
  const raw = search == null ? '' : String(search);
  const params = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
  const tabRaw = params.get('tab');
  const from = params.get('from');
  const to = params.get('to');
  return {
    tab: ANALYTICS_TABS.includes(tabRaw) ? tabRaw : 'stages',
    from: from && YMD_RE.test(from) ? from : null,
    to: to && YMD_RE.test(to) ? to : null,
  };
}

/**
 * Нормализованное состояние страницы: вкладка с учётом прав, период с дефолтом «7 дней»
 * и обменом from/to, если они перепутаны.
 */
export function resolveQuery({ tab, from, to } = {}, { canPayroll = true, today = new Date() } = {}) {
  const fallback = quickRange('7d', today);
  let nextFrom = from && YMD_RE.test(String(from)) ? String(from) : fallback.from;
  let nextTo = to && YMD_RE.test(String(to)) ? String(to) : fallback.to;
  if (nextFrom && nextTo && nextFrom > nextTo) {
    const swap = nextFrom;
    nextFrom = nextTo;
    nextTo = swap;
  }
  let nextTab = ANALYTICS_TABS.includes(tab) ? tab : 'stages';
  if (nextTab === 'payroll' && !canPayroll) nextTab = 'stages';
  return { tab: nextTab, from: nextFrom, to: nextTo };
}

export function buildQuery({ tab, from, to } = {}) {
  const params = new URLSearchParams();
  if (tab) params.set('tab', tab);
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  return params.toString();
}
